import React, { useState, useContext } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../authProvider/authProvider";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { app, host } from "../../../../config";
import { v4 as uuidv4 } from "uuid";
import CustomAlert from "components/generals/alert/CustomAlert";
import DynamicForm from "components/generals/dynamicForm/DynamicForm";

import "./openClaimsSystem.css";
import CircularProgress from "@mui/material/CircularProgress";
import { format, parse, parseISO } from "date-fns";
import { title } from "process";

const auth = getAuth();
const db = getFirestore(app);

const OpenClaimsLookupSystem: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }
  const { currentUser, suspended, role, selectedGroup } = authContext;

  const navigate = useNavigate();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  let tk: string;
  currentUser
    ?.getIdToken(false)
    .then((idToken: string) => {
      tk = idToken;
    })
    .catch((error: any) => {
      console.log(error);
    });

  if (suspended) {
    signOut(auth)
      .then(() => {
        console.log("Signed out");
        return <Navigate to="/login" />;
      })
      .catch((error) => {
        console.error("Error signing out", error);
      });
  }

  const stateList = [
    "Select...",
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ];

  const formSchema = {
    receivedRange: {
      title: "Received Date Range",
      fields: {
        dateReceivedStart: {
          form_type: "date",
          label: "Received Start Date",
          pair_with: "dateReceivedEnd",
        },
        dateReceivedEnd: {
          form_type: "date",
          label: "Received End Date",
        },
      },
    },
    processedRange: {
      title: "Processed Date Range",
      fields: {
        dateProcessedStart: {
          form_type: "date",
          label: "Processed Start Date",
          pair_with: "dateProcessedEnd",
        },
        dateProcessedEnd: {
          form_type: "date",
          label: "Processed End Date",
        },
      },
    },
    additionalFilters: {
      title: "Additional Filters",
      fields: {
        daysToProcess: {
          form_type: "number",
          label: "Days To Process",
        },
        paymentMadeTo: {
          form_type: "text input",
          label: "Payment Made To",
        },
        providerState: {
          form_type: "selection",
          label: "Provider State",
          selections: stateList,
        },
        facilityState: {
          form_type: "selection",
          label: "Provider State",
          selections: stateList,
        },
        memberState: {
          form_type: "selection",
          label: "Member State",
          selections: stateList,
        },
        systemOfRecord: {
          form_type: "selection",
          label: "System Of Record",
          selections: ["Select...", "WLT", "VBA"],
        },
        carrierId: {
          form_type: "selection",
          label: "Carrier ID",
          selections: [
            "Select...",
            "FSL",
            "SelfFunded",
            "Breckpoint",
            "Shelterpoint",
            "Nationwide",
            "AST",
            "MEC",
            "IHPMEC",
            "Zurich",
            "GERBER",
            "CHUBB",
            "MARKEL",
            "EVEREST",
            "HIIG",
            "IAIC Premium",
            "MULTI CARRIER",
          ],
        },
        groupId: {
          form_type: "text input",
          label: "Group ID",
        },
      },
    },
  };

  const handleSubmit = (formData) => {
    // e.preventDefault();
    setIsLoading(false);
    const hasAtLeastOneValue =
      formData["dateReceivedStart"] ||
      formData["dateReceivedEnd"] ||
      formData["dateProcessedStart"] ||
      formData["dateProcessedEnd"] ||
      formData["daysToProcess"] ||
      formData["paymentMadeTo"] ||
      formData["providerState"] ||
      formData["facilityState"] ||
      formData["memberState"] ||
      formData["systemOfRecord"] ||
      formData["carrierId"] ||
      formData["groupId"];

    if (!hasAtLeastOneValue) {
      setAlertMessage("Please fill in at least one search criteria!");
      setAlertOpen(true);
      return;
    }

    // Validate date ranges if either date in a pair is provided
    if (
      (formData["dateReceivedStart"] && !formData["dateReceivedEnd"]) ||
      (!formData["dateReceivedStart"] && formData["dateReceivedEnd"])
    ) {
      setAlertMessage("Please provide both start and end dates for Service Date Range!");
      setAlertOpen(true);
      return;
    }

    if (
      (formData["dateProcessedStart"] && !formData["dateProcessedEnd"]) ||
      (!formData["dateProcessedStart"] && formData["dateProcessedEnd"])
    ) {
      setAlertMessage("Please provide both start and end dates for Paid Date Range!");
      setAlertOpen(true);
      return;
    }

    // If dates are provided, validate the ranges
    if (formData["dateReceivedStart"] && formData["dateReceivedEnd"]) {
      const startDateObj = formData["dateReceivedStart"];
      const endDateObj = formData["dateReceivedEnd"];

      if (endDateObj < startDateObj) {
        setAlertMessage("Service End date must be after Service Start date!");
        setAlertOpen(true);
        return;
      }

      // Check if date range is more than 18 months
      const monthsDiff =
        (endDateObj.getFullYear() - startDateObj.getFullYear()) * 12 +
        (endDateObj.getMonth() - startDateObj.getMonth());

      if (monthsDiff > 18) {
        setAlertMessage("Service Date range cannot exceed 18 months!");
        setAlertOpen(true);
        return;
      }
    }

    // Validate paid date range if provided
    if (formData["dateProcessedStart"] && formData["dateProcessedEnd"]) {
      const paidStartDateObj = formData["dateProcessedStart"];
      const paidEndDateObj = formData["dateProcessedEnd"];

      if (paidEndDateObj < paidStartDateObj) {
        setAlertMessage("Paid End date must be after Paid Start date!");
        setAlertOpen(true);
        return;
      }
    }

    let holder: { [key: string]: any } = {};

    // Only add parameters that have values
    if (formData["dateReceivedStart"]) holder.dateReceivedStart = format(formData["dateReceivedStart"], "yyyy-MM-dd");
    if (formData["dateReceivedEnd"]) holder.dateReceivedEnd = format(formData["dateReceivedEnd"], "yyyy-MM-dd");
    if (formData["dateProcessedStart"])
      holder.dateProcessedStart = format(formData["dateProcessedStart"], "yyyy-MM-dd");
    if (formData["dateProcessedEnd"]) holder.dateProcessedEnd = format(formData["dateProcessedEnd"], "yyyy-MM-dd");
    if (formData["daysToProcess"]) holder.daysToProcess = formData["daysToProcess"];
    if (formData["paymentMadeTo"]) holder.paymentMadeTo = formData["paymentMadeTo"];
    if (formData["providerState"]) holder.providerState = formData["providerState"];
    if (formData["facilityState"]) holder.facilityState = formData["facilityState"];
    if (formData["memberState"]) holder.memberState = formData["memberState"];
    if (formData["systemOfRecord"]) holder.systemOfRecord = formData["systemOfRecord"];
    if (formData["carrierId"]) holder.carrierId = formData["carrierId"];
    if (formData["groupId"]) holder.groupId = formData["groupId"];

    let data = {
      job: "open-claims-test",
      className: "com.ugp.jobs.openClaims.OpenClaims",
      parameterString: JSON.stringify(holder),
    };

    setIsLoading(true);

    let params = new URLSearchParams(holder);

    fetch(`${host}/api/Reporting/Dataproc/Create/CreateDataProc`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify(data),
    })
      .then(async (response: Response) => {
        if (!response.ok) {
          await response.text().then((text) => {
            setAlertMessage(text || "An error occurred with your query");
            setAlertOpen(true);
          });
          return;
        } else {
          await response.text().then((res) => {
            setAlertMessage(
              "Your report has been requested. Please check your email in a few minutes for the results."
            );
            setAlertOpen(true);
          });
        }
      })
      .catch((error: any) => {
        console.log(error);
        setAlertMessage("An error has occurred when searching for Prompt Pay");
        setAlertOpen(true);
      })
      .finally(() => {
        setIsLoading(false);
        console.log("Processing finished");
      });
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="open-claims-container">
      <CustomAlert message={alertMessage} isOpen={isAlertOpen} onClose={() => setAlertOpen(false)} />
      <DynamicForm
        formSchema={formSchema}
        onSubmit={handleSubmit}
        title="Open Claims Report"
        submitButtonText="Submit"
      />
    </div>
  );
};

export default OpenClaimsLookupSystem;
